import PermissionButton, { currentCan } from '../../permissions/PermissionButton';
import React, { useState, useMemo, useRef } from 'react';
import { 
  DataGrid, 
  gridClasses, 
  useGridApiRef,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  styled,
  Pagination,
  PaginationItem,
  Tooltip
} from '@mui/material';
import { InfoCircleOutlined } from '@ant-design/icons';
import { message } from 'antd';

// Orange dot indicator for modified rows
const OrangeDot = styled('span')({
  display: 'inline-block',
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: '#ff8c00',
  marginRight: '6px'
});

// Custom pagination component with black theme
function CustomPagination() {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <Pagination
      color="primary"
      variant="outlined"
      shape="rounded"
      size="small"
      page={page + 1}
      count={pageCount}
      renderItem={(props) => <PaginationItem {...props} disableRipple />}
      onChange={(event, value) => apiRef.current.setPage(value - 1)}
      sx={{ 
        marginTop: '5px',
        display: 'flex',
        justifyContent: 'center',
        '& .MuiPaginationItem-root': {
          minWidth: '24px',
          height: '24px',
          fontSize: '0.75rem',
          color: '#000',
          borderColor: '#000',
          '&.Mui-selected': {
            backgroundColor: '#000',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#333',
            }
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          }
        }
      }}
    />
  );
}

// Page size constant
const PAGE_SIZE = 100;

const NestedAvoirTable = ({
  items = [], 
  handleSave,
  handleDelete,
  isValidated = false,
  codeEnt,
  onValueChange
}) => {
  const [confirmDelete, setConfirmDelete] = useState(null);
  const apiRef = useGridApiRef();
  const [paginationModel, setPaginationModel] = useState({
    pageSize: PAGE_SIZE,
    page: 0,
  });
  
  // Process data directly with useMemo instead of useEffect + state
  const processedRows = useMemo(() => {
    if (!Array.isArray(items)) return [];
    
    // Filter items to ensure they have CODE_ART (required for Data Grid row id)
    return items
      .filter(item => {
        if (!item || typeof item !== 'object') {
          return false;
        }
        
        if (!item.CODE_ART) {
          const hasNumericKeys = Object.keys(item).some(key => /^\d+$/.test(key));
          if (hasNumericKeys) {
            console.warn('Rejecting malformed item structure (array-like object):', item);
          }
          return false;
        }
        
        return true;
      });
  }, [items]);

  // Reference for the grid container
  const gridRef = useRef(null);
  
  // Check if row has been modified (comparing original vs current values)
  const isRowModified = (row) => {
    if (!row) return false;
    return row._isModified === true;
  };
  
  // Determine if a cell is editable based on status and field
  const isCellEditable = (params) => {
    // If avoir is validated, nothing is editable
    if (isValidated) {
      return false;
    }
    
    const field = params.field;
    
    // These fields are always editable (when not restricted by status)
    const editableFields = ['QTE', 'PV_TTC', 'REMISE'];
    return editableFields.includes(field);
  };
  
  // Process row update when editing is complete
  const processRowUpdate = (newRow, oldRow) => {
    try {
      const numericFields = ['QTE', 'PV_TTC', 'REMISE'];
      const processedRow = { ...newRow };
      const errors = [];

      numericFields.forEach(field => {
        if (field in processedRow) {
          const rawValue = processedRow[field];
          
          // Check for empty values
          if (rawValue === '' || rawValue === null || rawValue === undefined) {
            processedRow[field] = 0;
            return;
          }
          
          // Convert and validate
          const strValue = rawValue.toString().replace(',', '.');
          const numValue = Number(strValue);
          
          // Check if it's a valid number
          if (isNaN(numValue)) {
            errors.push(`${field === 'QTE' ? 'Quantité' : field === 'PV_TTC' ? 'Prix' : 'Remise'}: "${rawValue}" n'est pas un nombre valide`);
            return;
          }
          
          // Field-specific validation
          if (field === 'QTE') {
            if (numValue < 0) {
              errors.push('Quantité ne peut pas être négative');
            } else if (!Number.isInteger(numValue)) {
              errors.push('Quantité doit être un nombre entier');
            } else {
              processedRow[field] = numValue;
            }
          } else if (field === 'PV_TTC') {
            if (numValue < 0) {
              errors.push('Prix ne peut pas être négatif');
            } else {
              processedRow[field] = numValue;
            }
          } else if (field === 'REMISE') {
            if (numValue < 0 || numValue > 100) {
              errors.push('Remise doit être entre 0 et 100%');
            } else {
              processedRow[field] = numValue;
            }
          }
        }
      });

      // If there are errors, show message and rollback
      if (errors.length > 0) {
        message.error(errors[0], 2);
        return oldRow; // Rollback to original value
      }

      // If PV_TTC was changed, recalculate PV_HT and set REMISE to 0
      if (oldRow.PV_TTC !== processedRow.PV_TTC) {
        const newPvTtc = Number(processedRow.PV_TTC);
        const tva = Number(processedRow.TVA) || 0;
        const newPvHt = newPvTtc / (1 + tva / 100);

        processedRow.REMISE = 0;
        processedRow.PV_HT = Math.round(newPvHt * 100) / 100;
      }

      // Notify parent handler
      handleSave(processedRow);
      
      // Trigger recalculation if callback provided
      if (onValueChange) {
        onValueChange();
      }
      
      return processedRow;
    } catch (error) {
      console.error('Error in processRowUpdate:', error);
      message.error('Erreur lors de la mise à jour', 2);
      return oldRow; // Rollback on any error
    }
  };

  // Column definitions
  const columns = [
    {
      field: 'CODE_ART',
      headerName: 'Code Art',
      width: 150,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        if (!params || !params.row) return null;
        
        const article = params.row?.article || {};
        const uav = article.uav || 'N/A';
        const qtCart = article.qt_cart || 'N/A';
        const refOem = article.REF_OEM || params.row?.REF_OEM || 'N/A';
        
        const tooltipContent = (
          <Box sx={{ p: 0.5, fontSize: '12px' }}>
            <div><strong>UAV:</strong> {uav}</div>
            <div><strong>Qt/Carton:</strong> {qtCart}</div>
            <div><strong>REF OEM:</strong> {refOem}</div>
          </Box>
        );
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', justifyContent: 'flex-start' }}>
            {isRowModified(params.row) && <OrangeDot />}
            <span>{params.value || ''}</span>
            <Tooltip 
              title={tooltipContent} 
              arrow 
              placement="right"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'white',
                    color: 'black',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    border: '1px solid #e0e0e0',
                    '& .MuiTooltip-arrow': {
                      color: 'white',
                      '&::before': {
                        border: '1px solid #e0e0e0',
                      }
                    }
                  }
                }
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <InfoCircleOutlined 
                  style={{ 
                    fontSize: 14, 
                    color: '#1976d2', 
                    cursor: 'pointer'
                  }} 
                />
              </span>
            </Tooltip>
          </Box>
        );
      }
    },
    {
      field: 'ARTICLE',
      headerName: 'Article',
      flex: 1,
      minWidth: 200,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        return <div style={{ width: '100%', textAlign: 'left' }}>{params.value || ''}</div>;
      }
    },
    {
      field: 'qte_stk',
      headerName: 'Stock',
      width: 70,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        if (!params || !params.row) return <div style={{ width: '100%', textAlign: 'right' }}>0</div>;
        
        let value = 0;
        // First check if stock is stored directly in article.qte_stk
        if (params.row?.article?.qte_stk !== undefined && params.row?.article?.qte_stk !== null) {
          value = params.row.article.qte_stk;
        } else if (codeEnt && params.row?.article?.stocks && params.row.article.stocks.length > 0) {
          // If codeEnt is provided, find the stock for that specific entrepot
          const entrepotStock = params.row.article.stocks.find(s => s.code_ent === codeEnt);
          if (entrepotStock) {
            value = entrepotStock.qte_stk ?? entrepotStock.stock?.qte_stk ?? 0;
          }
        } else {
          // Fallback to first stock - handle both direct and nested formats
          const stock = params.row?.article?.stocks?.[0];
          value = stock?.qte_stk ?? stock?.stock?.qte_stk ?? 0;
        }
        
        return <div style={{ width: '100%', textAlign: 'right' }}>{value}</div>;
      }
    },
    {
      field: 'QTE',
      headerName: 'Qté',
      width: 100,
      align: 'right',
      headerAlign: 'right',
      editable: true,
      type: 'string',
      cellClassName: (params) => {
        if (!params || !params.row) return '';
        return isCellEditable(params) ? 'editable-cell' : '';
      },
      renderCell: (params) => {
        if (!params || !params.row) return <div style={{ width: '100%', textAlign: 'right' }}>0</div>;
        return <div style={{ width: '100%', textAlign: 'right' }}>{params.value || 0}</div>;
      }
    },
    {
      field: 'PV_TTC',
      headerName: 'Prix TTC',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      editable: true,
      type: 'string',
      cellClassName: 'price-cell',
      renderCell: (params) => {
        if (params.value === undefined || params.value === null) return <div style={{ width: '100%', textAlign: 'right' }}>0.00</div>;
        
        try {
          const numValue = Number(params.value);
          const display = isNaN(numValue) ? '0.00' : `${numValue.toFixed(2)}`;
          return <div style={{ width: '100%', textAlign: 'right' }}>{display}</div>;
        } catch (error) {
          return <div style={{ width: '100%', textAlign: 'right' }}>0.00</div>;
        }
      }
    },
    {
      field: 'REMISE',
      headerName: '%R',
      width: 100,
      align: 'right',
      headerAlign: 'right',
      editable: true,
      type: 'string',
      cellClassName: 'price-cell',
      renderCell: (params) => {
        if (params.value === undefined || params.value === null) return <div style={{ width: '100%', textAlign: 'right' }}>0.00%</div>;
        
        try {
          const numValue = Number(params.value);
          const display = isNaN(numValue) ? '0.00%' : `${numValue.toFixed(2)}%`;
          return <div style={{ width: '100%', textAlign: 'right' }}>{display}</div>;
        } catch (error) {
          return <div style={{ width: '100%', textAlign: 'right' }}>0.00%</div>;
        }
      }
    },
    {
      field: 'TVA',
      headerName: 'TVA',
      width: 70,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        if (params.value === undefined || params.value === null) return <div style={{ width: '100%', textAlign: 'right' }}>0%</div>;
        try {
          const numValue = Number(params.value);
          const display = isNaN(numValue) ? '0%' : `${numValue.toFixed(0)}%`;
          return <div style={{ width: '100%', textAlign: 'right' }}>{display}</div>;
        } catch (error) {
          return <div style={{ width: '100%', textAlign: 'right' }}>0%</div>;
        }
      }
    },
    {
      field: 'TotalTTC',
      headerName: 'Montant',
      width: 100,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        if (!params || !params.row) return <div style={{ width: '100%', textAlign: 'right' }}>0.00</div>;
        
        const row = params.row;
        const quantity = row.QTE || 0;
        const price = row.PV_TTC !== undefined && row.PV_TTC !== null ? Number(row.PV_TTC) : 0;
        const discount = row.REMISE !== undefined && row.REMISE !== null ? Number(row.REMISE) : 0;
        
        // Calculate the total amount
        const totalAmount = Number(quantity) * price * (1 - discount / 100);
        const display = isNaN(totalAmount) ? '0.00' : `${totalAmount.toFixed(2)}`;
        
        return <div style={{ width: '100%', textAlign: 'right' }}>{display}</div>;
      }
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        if (!params || !params.row) return null;
        
        const canDelete = currentCan('returns', 'update') && (!isValidated);
        
        return canDelete ? (
          <div 
            onClick={() => setConfirmDelete(params.row.CODE_ART)}
            style={{ 
              cursor: 'pointer', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: '100%',
              height: '100%'
            }}
          >
            <span style={{ 
              fontSize: '14px', 
              letterSpacing: '2px',
              fontWeight: 'bold'
            }}>...</span>
          </div>
        ) : null;
      }
    }
  ];

  return (
    <Box sx={{ 
      width: '100%',
      '& .editable-cell': {
        backgroundColor: 'rgba(25, 118, 210, 0.12)',
      },
      '& .price-cell': {
        color: '#1976d2',
      },
      '& .modified-row': {
        backgroundColor: 'rgba(255, 140, 0, 0.05)',
      }
    }}>
      <DataGrid
        ref={gridRef}
        apiRef={apiRef}
        rows={processedRows}
        columns={columns}
        getRowId={(row) => row.CODE_ART}
        rowHeight={28}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => console.error('Error updating row:', error)}
        isCellEditable={isCellEditable}
        disableColumnMenu
        
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[PAGE_SIZE]}
        slots={{
          pagination: CustomPagination,
        }}
        
        autoHeight={false}
        sx={{
          height: 600,
          maxHeight: '60vh',
          overflowY: 'auto',
          // Cell base styling
          [`& .${gridClasses.cell}`]: {
            fontSize: '0.8rem',
            padding: '1px 8px',
          },
          // Force text alignment for cells - MUI DataGrid alignment classes
          '& .MuiDataGrid-cell--textLeft': {
            justifyContent: 'flex-start',
            textAlign: 'left',
          },
          '& .MuiDataGrid-cell--textRight': {
            justifyContent: 'flex-end',
            textAlign: 'right',
          },
          '& .MuiDataGrid-cell--textCenter': {
            justifyContent: 'center',
            textAlign: 'center',
          },
          // Header alignment
          '& .MuiDataGrid-columnHeader--alignLeft .MuiDataGrid-columnHeaderTitle': {
            textAlign: 'left',
          },
          '& .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitle': {
            textAlign: 'right',
          },
          '& .MuiDataGrid-columnHeader--alignCenter .MuiDataGrid-columnHeaderTitle': {
            textAlign: 'center',
          },
          // Also force alignment on the columnHeader container
          '& .MuiDataGrid-columnHeader--alignRight': {
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              justifyContent: 'flex-end',
            },
          },
          '& .MuiDataGrid-columnHeader--alignLeft': {
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              justifyContent: 'flex-start',
            },
          },
          [`& .${gridClasses.cell}:focus`]: {
            outline: 'none',
            border: 'solid #000000 1px',
            borderRadius: '4px',
          },
          [`& .${gridClasses.row}:hover`]: {
            backgroundColor: 'rgba(115, 127, 139, 0.08)',
            cursor: 'pointer',
          },
          [`& .${gridClasses.row}.Mui-selected`]: {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(115, 127, 139, 0.12)',
            },
          },
          // CSS-based focus tracking - instant, no React state needed
          [`& .${gridClasses.row}:focus-within`]: {
            backgroundColor: 'rgba(25, 118, 210, 0.04)',
            '& > .MuiDataGrid-cell': {
              borderTop: '2px solid #000',
              borderBottom: '2px solid #000',
            },
            '& > .MuiDataGrid-cell:first-of-type': {
              borderLeft: '2px solid #000',
            },
            '& > .MuiDataGrid-cell:last-of-type': {
              borderRight: '2px solid #000',
            },
          },
          '& .MuiDataGrid-footerContainer': {
            border: 'none',
          }
        }}
        
        initialState={{
          sorting: {
            sortModel: [{ field: 'CODE_ART', sort: 'asc' }],
          },
        }}
        
        getRowClassName={(params) => {
          if (isRowModified(params.row)) {
            return 'modified-row';
          }
          return '';
        }}
        
        componentsProps={{
          cell: {
            onKeyDown: (e) => {
              if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key)) {
                return;
              }
              if (!e.ctrlKey && !e.metaKey && !e.altKey && e.key.length === 1) {
                e.defaultMuiPrevented = true;
              }
            }
          }
        }}
        
        editMode="cell"
        
        disableMultipleColumnsSorting
        disableMultipleSelection
        disableSelectionOnClick={false}
      />
      
      {/* Delete confirmation dialog */}
      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
      >
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer cet article?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <PermissionButton   permissionLibrary="mui" onClick={() => setConfirmDelete(null)}>
            Non
          </PermissionButton>
          <PermissionButton permissionModule="returns" permissionAction="update" permissionLibrary="mui"
            onClick={() => {
              handleDelete(confirmDelete);
              setConfirmDelete(null);
            }} 
            color="error" 
            autoFocus
          >
            Oui
          </PermissionButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NestedAvoirTable;
